import { setIsYellowMemberRightsModalShow } from '@/utils/fetchers/base'
import { useDeviceType } from '@/utils/hooks/useMediaQuery'
import { useIsDarkTheme } from '@/utils/hooks/useTheme'
import { getParentRadius } from '@/utils/ui'
import { css } from '@emotion/react'
import { CircleButton } from '@ruguoapp/jike-fe-components'
import { AddIcon } from '@ruguoapp/jike-fe-icons'
import { AnimatePresence, motion } from 'motion/react'
import type { HTMLAttributes } from 'react'
import ClickWrapper from '../ClickWrapper'
import JellowLogo from '../Icons/JellowLogo'
import JikeLogo from '../Icons/JikeLogo'
import JikeYellowLogoIcon from '../Icons/JikeYellowLogoIcon'
import { usePostFormModal } from '../Post/PostFormModal'
import SidebarTabs from './SidebarTabs'

export interface SidebarProps extends HTMLAttributes<HTMLDivElement> {}

export default function Sidebar({ ...props }: SidebarProps) {
	const { open } = usePostFormModal()
	const isDarkMode = useIsDarkTheme()
	const { isMobile } = useDeviceType()
	return (
		<>
			<div
				className="navbar"
				css={[
					css`
						position: fixed;
						top: 0;
						left: 0;
						z-index: 3;
						width: 84px;
						height: 100vh;
						padding: 20px;
						box-shadow: 0.5px 0 0 0 var(--tint-separator);
						background-color: var(--bg-body-1);
						display: flex;
						flex-direction: column;
						justify-content: space-between;
						align-items: center;
					`,
					isMobile &&
						css`
							bottom: calc(env(safe-area-inset-bottom, 20px) + 20px);
							left: 0;
							right: 0;
							top: unset;
							width: fit-content;
							height: fit-content;
							flex-flow: row nowrap;
							justify-content: center;
							padding: 0;
							max-width: calc(100vw - 40px);
							background: unset;
							box-shadow: unset;
							margin: 0 auto;

							.sidebar-tabs {
								padding: 5px;
								border-radius: ${getParentRadius(10, 5)}px;
								background: var(--bg-body-1);
								box-shadow: 0 4px 30px var(--tint-shadow-1);
							}

							.jike-logo,
							.sidebar-footer {
								display: none;
							}

							.create-post-button {
								display: none;
							}
						`,
				]}
			>
				<a
					href="/"
					aria-label="即刻 Logo"
					css={css`
						display: block;
						margin-bottom: 36px;
					`}
					className="jike-logo"
				>
					<AnimatePresence mode="wait" initial={false}>
						{isDarkMode ? (
							<motion.div
								key="dark"
								initial={{ y: 10, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -10, opacity: 0 }}
								transition={{ duration: 0.2, type: 'spring', damping: 20 }}
							>
								<JellowLogo
									css={css`
										border: 1px solid var(--tint-separator);
										border-radius: 8px;
										height: 37px;
									`}
								/>
							</motion.div>
						) : (
							<motion.div
								key="light"
								initial={{ y: 10, opacity: 0 }}
								animate={{ y: 0, opacity: 1 }}
								exit={{ y: -10, opacity: 0 }}
								transition={{ duration: 0.2, type: 'spring', damping: 20 }}
							>
								<JikeLogo
									css={css`
										height: 37px;
									`}
								/>
							</motion.div>
						)}
					</AnimatePresence>
				</a>

				<SidebarTabs
					className="sidebar-tabs"
					css={css`
						flex-direction: inherit;
					`}
				/>

				<CircleButton
					aria-label="创建动态"
					onClick={() => open()}
					size="sm"
					appearance="primary"
					className="create-post-button"
					css={css`
						cursor: pointer;
						margin-top: 52px;
					`}
					icon={
						<AddIcon
							css={css`
								color: var(--solid-gray-4);
							`}
						/>
					}
				/>

				<footer
					className="sidebar-footer"
					css={css`
						margin-top: auto;
					`}
				>
					<ClickWrapper
						whileTap={{
							scale: 0.98,
						}}
						onClick={() => {
							setIsYellowMemberRightsModalShow(true)
						}}
					>
						<JikeYellowLogoIcon
							css={css`
								height: 40px;
								width: 40px;
							`}
						/>
					</ClickWrapper>
				</footer>
			</div>

			{isMobile && (
				<CircleButton
					aria-label="创建动态"
					onClick={() => open()}
					size="md"
					appearance="primary"
					css={css`
						position: fixed;
						bottom: calc(env(safe-area-inset-bottom, 20px) + 22px);
						right: 20px;
						z-index: 3;
						cursor: pointer;
					`}
					icon={
						<AddIcon
							css={css`
								color: var(--solid-gray-4);
							`}
						/>
					}
				/>
			)}
		</>
	)
}
